import type { PasswordLoginRoute } from "@/app/api/auth/password-login/route";
import {
  getSSORedirectURL,
  probeSSODomainWithRetry,
} from "@/components/auth-ui/sso-redirect";
import type { PasswordLoginMode } from "@/components/auth-ui/types";
import { assertNever } from "@/packages/ts-utils";
import { getMfaUiState } from "@/utils/auth/mfa";
import { postJson } from "@/utils/routeUtils";
import type { Database } from "@/utils/types";
import { SupabaseClient } from "@supabase/supabase-js";
import { mutationOptions } from "@tanstack/react-query";

type SigninInput = {
  email: string;
  password: string;
  confirmedNoSso?: { email: string; kind: "none" };
};

type SigninResult =
  | { kind: "signedIn" }
  | { kind: "ssoRedirect"; url: string }
  | { kind: "mfaChallenge" };

export const signinMutationOptions = ({
  supabaseClient,
  ssoProbeClient,
  getTurnstileToken,
  passwordLoginMode = "turnstile",
  deeplink,
}: {
  supabaseClient: SupabaseClient<Database>;
  ssoProbeClient: SupabaseClient<Database>;
  getTurnstileToken: () => Promise<string | undefined>;
  passwordLoginMode?: PasswordLoginMode;
  deeplink?: string | null;
}) =>
  mutationOptions({
    mutationFn: async ({
      email,
      password,
      confirmedNoSso,
    }: SigninInput): Promise<SigninResult> => {
      const probe =
        confirmedNoSso?.email === email
          ? confirmedNoSso
          : await probeSSODomainWithRetry({
              email,
              supabaseClient: ssoProbeClient,
              getTurnstileToken,
              passwordLoginMode,
            });
      switch (probe.kind) {
        case "sso": {
          const ssoToken = await getTurnstileToken();
          const ssoRedirectUrl = await getSSORedirectURL(
            email,
            supabaseClient,
            ssoToken,
            deeplink
          );
          if (!ssoRedirectUrl) {
            throw new Error(
              "Could not start single sign-on for this email. Please try again."
            );
          }
          return { kind: "ssoRedirect", url: ssoRedirectUrl };
        }
        case "none":
          break;
        default:
          assertNever(probe);
      }

      switch (passwordLoginMode) {
        case "trustedIp": {
          const result = await postJson<
            "/api/auth/password-login",
            PasswordLoginRoute["input"]
          >(
            "/api/auth/password-login",
            { action: "signIn", email, password },
            { skipLogs: true }
          );
          if (!result.success)
            throw new Error(
              result.error ?? "Could not sign in. Please try again."
            );
          switch (result.data.action) {
            case "signIn": {
              const { error } = await supabaseClient.auth.setSession(
                result.data.session
              );
              if (error) throw new Error(error.message);
              break;
            }
            case "probe":
              throw new Error("Unexpected response to password login");
            default:
              assertNever(result.data);
          }
          break;
        }
        case "turnstile": {
          const authToken = await getTurnstileToken();
          if (!authToken) {
            throw new Error(
              "Security verification failed. Please refresh and try again."
            );
          }
          const { error } = await supabaseClient.auth.signInWithPassword({
            email,
            password,
            options: { captchaToken: authToken },
          });
          if (error) throw new Error(error.message);
          break;
        }
        default:
          assertNever(passwordLoginMode);
      }

      const { isStepUpRequired } = await getMfaUiState({
        supabase: supabaseClient,
      });
      if (isStepUpRequired) {
        return { kind: "mfaChallenge" };
      }

      return { kind: "signedIn" };
    },
  });
